import { waitForElement } from "./waitForElement.util";

/**
 * Creates a modal element based on the content and attributes of a specified element.
 * @param RouteHandlerCustomElementID - The ID of the element containing the modal content.
 * @param openAfterCreate - true/false - Whether to open the modal after creating it.
 */
export async function createModal(RouteHandlerCustomElementID: string, openAfterCreate: boolean = false): void {
    // Log the script ID
    console.log('Script ID:', RouteHandlerCustomElementID);

    // Find the element with the specified ID
    const element = document.querySelector(`#${RouteHandlerCustomElementID}`) as HTMLElement | null;
    await waitForElement(`#${RouteHandlerCustomElementID}`).then((elementExists) => {
        if (!elementExists) {
            console.error(`Element with ID ${RouteHandlerCustomElementID} not found.`);
            return;
        }
    });

    if (!element) {
        console.error(`Element with ID ${RouteHandlerCustomElementID} not found.`);
        return;
    }

    // Find the .modal-zone element within the specified element
    const modalZone = element.querySelector('.modal-zone') as HTMLElement | null;
    if (!modalZone) {
        console.error(`Element with class 'modal-zone' not found within #${RouteHandlerCustomElementID}.`);
        return;
    }

    console.log(modalZone);

    // Get the original content of the modal-zone
    const originalContent = modalZone.innerHTML;

    // Generate random modal ID
    const modalId = `modal-${Math.floor(Math.random() * 1000000)}`;

    // Default attributes for the modal
    const defaultAttributes: { [key: string]: string } = {
        id: modalId,
        class: 'modal fade new-modal-style',
        tabindex: '-1',
        ariaLabelledby: modalId,
        style: 'padding-right: 15px; background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(3px); position: absolute; top: 0; left: 0; width: 100%; height: 100%; display: none;',
        ariaModal: 'true',
        role: 'dialog',
    };

    // Convert modalZone attributes into an object
    const modalZoneAttributes: { [key: string]: string } = Array.from(modalZone.attributes).reduce(
        (acc: { [key: string]: string }, attr) => {
            acc[attr.name] = attr.value;
            return acc;
        },
        {}
    );

    // Remove "modal-zone" from class before merging
    if (modalZoneAttributes.class) {
        modalZoneAttributes.class = modalZoneAttributes.class
            .split(' ')
            .filter(className => className !== 'modal-zone')
            .join(' ');
    }

    // Merge default and modalZone attributes
    const mergedAttributes: { [key: string]: string } = { ...defaultAttributes, ...modalZoneAttributes };

    // Handle class attribute merging (append instead of replace)
    if (modalZoneAttributes.class && defaultAttributes.class) {
        mergedAttributes.class = `${defaultAttributes.class} ${modalZoneAttributes.class}`;
    }

    // Handle style attribute merging (append instead of replace)
    if (modalZoneAttributes.style && defaultAttributes.style) {
        mergedAttributes.style = `${defaultAttributes.style} ${modalZoneAttributes.style}`;
    }

    // Create an array of merged attributes in name="value" format
    const attributesArray: string[] = Object.entries(mergedAttributes).map(
        ([name, value]) => `${name}="${value}"`
    );

    console.log('attributes', attributesArray);

    // Remove all attributes from modalZone except for 'class'
    Array.from(modalZone.attributes).forEach(attr => {
        if (attr.name !== 'class') {
            modalZone.removeAttribute(attr.name);
        }
    });

    // Ensure only the 'modal-zone' class remains
    modalZone.className = 'modal-zone';

    // Create the modal HTML structure
    const modalHtml = `
        <div class="modal fade new-modal-style" ${attributesArray.join(' ')}>
            <div class="modal-dialog modal-fullscreen">
                <div class="modal-content">
                    <div class="modal-body">
                        ${originalContent}
                    </div>
                </div>
            </div>
        </div>
    `;

    // Optionally, clear the content from .modal-zone after copying it to the modal
    modalZone.innerHTML = '';

    // Create a div to hold the modal and insert it into the body
    const modalWrapper = document.createElement('div');
    modalWrapper.innerHTML = modalHtml;

    const modalElement = modalWrapper.firstElementChild;
    if (modalElement) {
        document.body.appendChild(modalElement);
    } else {
        console.error('Failed to create modal element.');
        return;
    }

    if (openAfterCreate) {
        modalElement.classList.add('show');
    }

}